/**
 * Purchase orders. One order can be billed across several supplier
 * invoices, so an invoice points at an order and never the reverse.
 *
 * Everything reconciles in base units, which is what lets an order placed
 * in Cards be filled by a delivery in Boxes.
 */

export type OrderStatus = "open" | "closed" | "cancelled";

export interface OrderLine {
  productId: string;
  productName?: string;
  scale?: string;
  packName?: string | null;
  packQuantity?: number;
  unitsPerPack?: number;
  packPrice?: number;
  quantity?: number;
  price?: number;
  receivedQuantity?: number;
  outstanding?: number;
  outstandingPacks?: number | null;
  overReceived?: number;
}

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  open: "Open",
  closed: "Closed",
  cancelled: "Cancelled",
};

/**
 * Base units are the authoritative figure. The pack is shown beside it
 * only when it divides evenly - 24 tablet is 2 Card, but 20 tablet is not
 * 1.67 Card and reads better without.
 */
export const describeOutstanding = (line: OrderLine): string => {
  const outstanding = Number(line?.outstanding) || 0;
  const unit = line?.scale || "";
  const base = `${outstanding} ${unit}`.trim();
  if (!outstanding || !line?.outstandingPacks || !line?.packName) return base;
  return `${base} (${line.outstandingPacks} ${line.packName})`;
};

/** "10 Card × 12 tablet" for a packed line, plain base units otherwise. */
export const describeOrdered = (line: OrderLine): string => {
  const unit = line?.scale || "";
  if (!line?.packName) return `${line?.quantity ?? 0} ${unit}`.trim();
  return `${line.packQuantity} ${line.packName} × ${line.unitsPerPack} ${unit}`.trim();
};

export const receivedPercent = (
  totalReceived: number | undefined,
  totalOrdered: number | undefined
): number => {
  const ordered = Number(totalOrdered) || 0;
  if (ordered <= 0) return 0;
  const received = Number(totalReceived) || 0;
  return Math.min(Math.round((received / ordered) * 100), 100);
};

/**
 * Lines to start a new invoice with when an order is chosen. Defaults to
 * what is still outstanding rather than the full order, since earlier
 * invoices may already have covered part of it, and skips lines that are
 * fully received.
 */
export const orderLinesToInvoiceLines = (order: {
  products?: OrderLine[];
}): any[] =>
  (order?.products || [])
    .filter((line) => (Number(line?.outstanding) || 0) > 0)
    .map((line) => {
      const unitsPerPack = Number(line?.unitsPerPack) || 1;
      const outstanding = Number(line?.outstanding) || 0;
      // Prefill whole packs only when the remainder divides evenly;
      // otherwise fall back to the base unit so nothing is rounded away.
      const wholePacks =
        unitsPerPack > 1 && outstanding % unitsPerPack === 0
          ? outstanding / unitsPerPack
          : null;
      const packQuantity = wholePacks ?? outstanding;
      const effectiveUnits = wholePacks ? unitsPerPack : 1;
      const packPrice = Number(line?.packPrice) || 0;

      return {
        productId: line.productId,
        productName: line.productName,
        scale: line.scale,
        packName: wholePacks ? line.packName ?? null : null,
        packQuantity,
        unitsPerPack: effectiveUnits,
        packPrice: wholePacks ? packPrice : packPrice / unitsPerPack || 0,
        quantity: packQuantity * effectiveUnits,
        price: wholePacks
          ? packPrice * packQuantity
          : (packPrice / unitsPerPack || 0) * packQuantity,
        unitPrice: unitsPerPack > 0 ? packPrice / unitsPerPack || 0 : 0,
        paid: false,
      };
    });

/**
 * Products the supplier sent that were never on the order. Allowed -
 * suppliers add things, and blocking them would push staff to skip the
 * link entirely - but marked so the comparison still means something.
 */
export const markOffOrderLines = (
  lines: any[],
  order: { products?: OrderLine[] } | null
): any[] => {
  if (!order) return (lines || []).map((line) => ({ ...line, offOrder: false }));
  const ordered = new Set(
    (order.products || []).map((line) => String(line.productId))
  );
  return (lines || []).map((line) => ({
    ...line,
    offOrder: !ordered.has(String(line.productId)),
  }));
};

/** Same page size as the purchase list and the other paged lists. */
export const ORDER_PAGE_SIZE = 20;

export interface OrderRow {
  orderNo?: string;
  supplierName?: string;
  supplierCompany?: string;
  orderDate?: string;
  [key: string]: any;
}

/** Order number, supplier, or the supplier's company - whichever staff hold. */
export const matchesOrderSearch = (row: OrderRow, search: string): boolean => {
  const term = (search || "").trim().toLowerCase();
  if (!term) return true;
  return (
    String(row?.orderNo ?? "").toLowerCase().includes(term) ||
    String(row?.supplierName ?? "").toLowerCase().includes(term) ||
    String(row?.supplierCompany ?? "").toLowerCase().includes(term)
  );
};

/** orderDate is stored ISO (yyyy-mm-dd), so a string compare is the range. */
export const matchesOrderDateRange = (
  row: OrderRow,
  from: string,
  to: string
): boolean => {
  if (!from && !to) return true;
  const date = String(row?.orderDate ?? "").slice(0, 10);
  if (!date) return false;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
};

export interface OrderFilters {
  search?: string;
  from?: string;
  to?: string;
}

/**
 * Status stays with the server - it is what findAllPurchaseOrder is asked
 * for - so only the text and date narrowing happens here.
 */
export const filterOrders = (
  rows: OrderRow[],
  { search = "", from = "", to = "" }: OrderFilters
): OrderRow[] =>
  (rows || []).filter(
    (row) =>
      matchesOrderSearch(row, search) && matchesOrderDateRange(row, from, to)
  );
